import React, { useEffect, useState } from 'react';
import {
    Box,
    VStack,
    HStack,
    Text,
    Flex,
    Icon,
    Grid,
    GridItem,
} from '@chakra-ui/react';
import { DialogRoot, DialogContent } from '../../ui/dialog';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import { FaWhatsapp } from 'react-icons/fa';
import ButtonBox from '../buttons/ButtonBox';
import SelectBox from '../fields/SelectBox';
import { selectTemplate, resetWhatsappState, setWhatsappData, clearTemplate, setValidationErrors, clearValidationErrors, loadTemplatesRequest } from '../../../store/whatsappSlice';
import { loadCompanyInfoRequest } from '../../../store/companyInfoSlice';
import { useColorModeValue } from '../../ui/color-mode';
import { validateWhatsappForm, hasValidationErrors } from '../../../utils/whatsappValidation';
import InputBox from '../fields/InputBox';

const SendWhatsappDialog = ({ isOpen, onClose, recipientPhone, complaintId, complaintData, useCase, useCaseId }) => {
    const dispatch = useDispatch();
    const { templates, selectedTemplate, whatsappData, validationErrors, selectedPhone } = useSelector(state => state.whatsapp);

    const { control, reset } = useForm({
        defaultValues: {
            whatsappTemplate: ''
        }
    });

    const bgColor = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const textColor = useColorModeValue('gray.700', 'gray.200');
    const labelColor = useColorModeValue('gray.600', 'gray.400');
    const inputBg = useColorModeValue('white', 'gray.700');
    const headerBg = useColorModeValue('green.600', 'green.700');
    const errorColor = useColorModeValue('red.500', 'red.400');

    useEffect(() => {
        if (isOpen) {
            reset({ whatsappTemplate: '' });
            dispatch(selectTemplate(null));
            if (recipientPhone) {
                dispatch(setWhatsappData({
                    to: recipientPhone,
                    body: '',
                    header: ''
                }));
            }
            dispatch(clearValidationErrors());
            dispatch(loadCompanyInfoRequest());
            if (complaintId) {
                dispatch(loadTemplatesRequest({ utility: 'COMPLAINT' }));
            } else {
                dispatch(loadTemplatesRequest());
            }
        }
    }, [isOpen, recipientPhone, reset, dispatch]);

    const handleTemplateSelect = (templateId) => {
        console.log(templateId)
        if (!templateId || (Array.isArray(templateId) && templateId.length === 0)) return;

        const idVal = Array.isArray(templateId) ? templateId[0] : templateId;
        dispatch(selectTemplate(idVal));

        const template = templates.find(t => String(t.id) === String(idVal));
        if (template) {
            dispatch(setWhatsappData({
                body: template.body || '',
                header: template.headerText || ''
            }));
        }
        dispatch(clearValidationErrors());
    };

    const watchedTemplate = useWatch({ control, name: 'whatsappTemplate' });
    useEffect(() => {
        if (watchedTemplate && (!Array.isArray(watchedTemplate) || watchedTemplate.length > 0)) {
            handleTemplateSelect(watchedTemplate);
        }
    }, [watchedTemplate, templates]);

    const handleResetTemplate = () => {
        dispatch(clearTemplate());
        reset({ whatsappTemplate: [] });
        dispatch(setWhatsappData({ body: '', header: '' }));
        dispatch(clearValidationErrors());
    };

    const handleClose = () => {
        dispatch(resetWhatsappState());
        onClose();
    };

    const handleSendWhatsapp = () => {
        const errors = validateWhatsappForm(whatsappData, templateParams, headerVariables, bodyVariables);
        if (hasValidationErrors(errors)) {
            dispatch(setValidationErrors(errors));
            return;
        }

        dispatch(clearValidationErrors());

        const messagePayload = {
            phone: whatsappData.to,
            displayPhoneNumberId: selectedPhone?.id || selectedPhone?.wabaPhoneId || '820888807778797',
            messageType: 'TEMPLATE',
            templateId: selectedTemplate?.id || null,
            useCase,
            useCaseId,
            templateParams: templateParams,
        };

        if (templateParams)

        dispatch({
            type: "SEND_WHATSAPP",
            payload: messagePayload
        });
        handleClose();
    };

    const formatContent = (content) => {
        if (!content) return '';
        let formatted = content;
        formatted = formatted.replace(/{customerName}/g, complaintData?.customerName || 'Customer');
        formatted = formatted.replace(/{complaintId}/g, complaintId);
        formatted = formatted.replace(/{productName}/g, complaintData?.productName || 'Product');
        return formatted;
    };

    const templateOptions = templates.map(template => ({
        label: template.name,
        value: template.id.toString(),
        description: template.description
    }));

    const regex = /\{\{(.*?)\}\}/g;
    const headerIterator = whatsappData?.header?.matchAll(regex) ?? [];
    const headerVariables = Array.from(headerIterator, match => match[1]);

    const bodyIterator = whatsappData?.body?.matchAll(regex) ?? [];
    const bodyVariables = Array.from(bodyIterator, match => match[1]);

    const [templateParams, setTemplateParams] = useState({ header: {}, body: {} });

    useEffect(() => {
        setTemplateParams(prev => ({
            header: headerVariables.reduce((acc, v) => ({ ...acc, [v]: prev.header?.[v] ?? '' }), {}),
            body: bodyVariables.reduce((acc, v) => ({ ...acc, [v]: prev.body?.[v] ?? '' }), {})
        }));
    }, [headerVariables.join('|'), bodyVariables.join('|')]);

    return (
        <DialogRoot open={isOpen} onOpenChange={({ open }) => !open && handleClose()}>
            <DialogContent
                maxWidth="2xl"
                bg={bgColor}
                borderRadius="lg"
                placement="center"
                mt="120px"
                p={0}
                overflow="hidden"
            >
                {/* Header */}
                <Box bg={headerBg} color="white" p={6}>
                    <HStack spacing={3}>
                        <Icon as={FaWhatsapp} boxSize={5} />
                        <VStack align="start" spacing={0}>
                            <Text fontSize="lg" fontWeight="bold">
                                Send WhatsApp Message to Customer
                            </Text>
                        </VStack>
                    </HStack>
                </Box>

                {/* Body */}
                <Flex height="500px" bg={bgColor}>
                    <Box flex={1} p={6} borderRight="1px solid" borderColor={borderColor} overflowY="auto">
                        <VStack spacing={6} align="stretch">
                            {/* Template Selection */}
                            <Box>
                                <SelectBox
                                    control={control}
                                    name="whatsappTemplate"
                                    label="Choose WhatsApp Template"
                                    placeholder="Select a template"
                                    items={templateOptions}
                                    onChange={handleTemplateSelect}
                                />
                            </Box>

                            {/* Template Preview */}
                            <Box>
                                <Text fontSize="sm" fontWeight="semibold" mb={2} color={labelColor}>
                                    Template Preview:
                                </Text>

                                {whatsappData.header && (
                                    <Box mb={3} p={3} borderRadius="md" bg={inputBg} border="1px solid" borderColor={borderColor}>
                                        <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                                            {formatContent(whatsappData.header)}
                                        </Text>
                                    </Box>
                                )}

                                <Box p={3} borderRadius="md" minHeight="160px" bg={inputBg} border="1px solid" borderColor={validationErrors.body ? errorColor : borderColor}>
                                    <Text whiteSpace="pre-wrap" color={textColor} fontSize="sm">
                                        {formatContent(whatsappData.body) || 'Select a template to preview the message.'}
                                    </Text>
                                </Box>

                                <Box mt={10}>
                                    <Grid mt={4} templateColumns="repeat(2, 1fr)" gap={4}>
                                        {headerVariables.length > 0 && headerVariables.map((variable, index) => (
                                            <GridItem key={`header-${index}`}>
                                                <InputBox
                                                    label={`Header Variable: ${variable}`}
                                                    value={templateParams.header[variable] ?? ''}
                                                    register={{ onChange: (e) => setTemplateParams(prev => ({ ...prev, header: { ...prev.header, [variable]: e.target.value } })) }}
                                                    invalid={!!(validationErrors.templateParams && validationErrors.templateParams.header && validationErrors.templateParams.header[variable])}
                                                    errorText={validationErrors.templateParams && validationErrors.templateParams.header && validationErrors.templateParams.header[variable]}
                                                />
                                            </GridItem>
                                        ))}
                                    </Grid>
                                    <Grid mt={4} templateColumns="repeat(2, 1fr)" gap={4}>
                                        {bodyVariables.length > 0 && bodyVariables.map((variable, index) => (
                                            <GridItem key={`body-${index}`}>
                                                <InputBox
                                                    label={`Body Variable: ${variable}`}
                                                    value={templateParams.body[variable] ?? ''}
                                                    register={{ onChange: (e) => setTemplateParams(prev => ({ ...prev, body: { ...prev.body, [variable]: e.target.value } })) }}
                                                    invalid={!!(validationErrors.templateParams && validationErrors.templateParams.body && validationErrors.templateParams.body[variable])}
                                                    errorText={validationErrors.templateParams && validationErrors.templateParams.body && validationErrors.templateParams.body[variable]}
                                                />
                                            </GridItem>
                                        ))}
                                    </Grid>
                                </Box>

                                {validationErrors.body && (
                                    <Text fontSize="xs" color={errorColor} mt={1}>
                                        {validationErrors.body}
                                    </Text>
                                )}
                            </Box>

                        </VStack>
                    </Box>
                </Flex>

                {/* Footer */}
                <Flex
                    p={6}
                    borderTop="1px solid"
                    borderColor={borderColor}
                    justify="space-between"
                    gap={3}
                    bg={bgColor}
                >
                    <ButtonBox
                        name="Reset Template"
                        variant="outline"
                        colorScheme="orange"
                        onClick={handleResetTemplate}
                        disabled={!selectedTemplate}
                    />
                    <HStack gap={3}>
                        <ButtonBox
                            name="Cancel"
                            variant="outline"
                            colorScheme="blue"
                            onClick={handleClose}
                        />
                        <ButtonBox
                            name="Send WhatsApp Message"
                            colorPalette="green"
                            variant="solid"
                            onClick={handleSendWhatsapp}
                        />
                    </HStack>
                </Flex>
            </DialogContent>
        </DialogRoot>
    );
};

export default SendWhatsappDialog;